import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page,
  Card,
  BlockStack,
  InlineGrid,
  Text,
  Button,
  TextField,
  Select,
  Modal,
  FormLayout,
  LegacyCard,
  IndexTable,
  Badge,
  Spinner,
  Divider,
  InlineStack,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatPrice, formatDate } from "../../utility/funcs";
import { UserAuth } from "../../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import moment from "moment";
import PaymentDetailModal from "../Invoicing/PaymentDetailModal";

function CashDaily() {
  const { session } = UserAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [movements, setMovements] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  // Payment id whose invoices/reservations detail is shown; null = modal closed.
  const [detailTxnId, setDetailTxnId] = useState(null);
  const [movementDetail, setMovementDetail] = useState({ open: false, movement: null });

  const [newMovement, setNewMovement] = useState({
    type: "expense",
    amount: "",
    description: "",
    payment_method: "Efectivo",
    account_id: "",
  });

  const [newTransfer, setNewTransfer] = useState({
    amount: "",
    description: "",
    from_account: "",
    to_account: "",
  });

  const fetchData = async () => {
    setLoading(true);

    // Monterrey timezone is UTC-6, so day boundaries are 06:00 and next-day 06:00 in UTC
    const startOfDay = `${date}T06:00:00.000Z`;
    const endOfDay = `${moment(date).add(1, "day").format("YYYY-MM-DD")}T06:00:00.000Z`;

    const [movRes, accRes, txnRes] = await Promise.all([
      supabase
        .from("cash_movements")
        .select("*, account:account_id(name), to_account:to_account_id(name)")
        .gte("created_at", startOfDay)
        .lt("created_at", endOfDay)
        .order("created_at", { ascending: false }),
      supabase
        .from("transaction_accounts")
        .select("*")
        .order("order"),
      supabase
        .from("transactions")
        .select("*, account(name, type)")
        .gte("created_at", startOfDay)
        .lt("created_at", endOfDay)
        .order("created_at", { ascending: false }),
    ]);

    // Exclude the internal "Crédito de cliente" account — applying credit isn't
    // a real cash movement (the money entered the bank once via the original payment).
    if (accRes.data) setAccounts(accRes.data.filter((a) => a.type !== "credit"));

    // Batch-resolve client names from billing_entity_type/_id (and fall back to
    // legacy `customer` column when billing_entity_* is missing).
    const customerIds = new Set();
    const companyIds = new Set();
    (txnRes.data || []).forEach((t) => {
      if (t.billing_entity_type === "company" && t.billing_entity_id) {
        companyIds.add(t.billing_entity_id);
      } else if (t.billing_entity_type === "customer" && t.billing_entity_id) {
        customerIds.add(t.billing_entity_id);
      } else if (t.customer) {
        customerIds.add(t.customer);
      }
    });
    // cash_movements may reference a reservation but carry no client — resolve
    // the client through the reservation so the row isn't blank (e.g. deposit
    // refunds: "Devolución de depósito - Reserva #N").
    const movResIds = [
      ...new Set((movRes.data || []).map((m) => m.reservation_id).filter(Boolean)),
    ];
    let resById = {};
    if (movResIds.length > 0) {
      const { data: resRows } = await supabase
        .from("reservations")
        .select("id, customer, company")
        .in("id", movResIds);
      resById = Object.fromEntries((resRows || []).map((r) => [r.id, r]));
      (resRows || []).forEach((r) => {
        if (r.company) companyIds.add(r.company);
        else if (r.customer) customerIds.add(r.customer);
      });
    }

    let customersById = {};
    let companiesById = {};
    if (customerIds.size > 0) {
      const { data: customers } = await supabase
        .from("customers")
        .select("id, first_name, last_name")
        .in("id", Array.from(customerIds));
      customersById = Object.fromEntries(
        (customers || []).map((c) => [c.id, `${c.first_name || ""} ${c.last_name || ""}`.trim()])
      );
    }
    if (companyIds.size > 0) {
      const { data: companies } = await supabase
        .from("companies")
        .select("id, name")
        .in("id", Array.from(companyIds));
      companiesById = Object.fromEntries((companies || []).map((c) => [c.id, c.name]));
    }

    // Merge transactions and cash_movements into a single timeline
    const allMovements = [];

    if (txnRes.data) {
      txnRes.data.forEach((t) => {
        // Skip internal credit-account movements (not real cash in/out).
        if (t.account?.type === "credit") return;
        // Map transaction types to movement types
        let movType;
        if (t.type === "refund") movType = "expense";
        else if (t.isDeposit) movType = "deposit";
        else movType = "income";

        const label = t.type === "refund"
          ? "Reembolso"
          : t.isDeposit
            ? "Depósito"
            : "Pago";

        let entityType = null;
        let entityId = null;
        let entityName = "";
        if (t.billing_entity_type === "company" && t.billing_entity_id) {
          entityType = "company";
          entityId = t.billing_entity_id;
          entityName = companiesById[t.billing_entity_id] || "Empresa";
        } else if (t.billing_entity_type === "customer" && t.billing_entity_id) {
          entityType = "customer";
          entityId = t.billing_entity_id;
          entityName = customersById[t.billing_entity_id] || "Cliente";
        } else if (t.customer) {
          entityType = "customer";
          entityId = t.customer;
          entityName = customersById[t.customer] || "Cliente";
        }

        allMovements.push({
          id: `txn-${t.id}`,
          time: t.created_at,
          type: movType,
          description: label,
          amount: t.amount,
          payment_method: t.account?.name || "",
          account: t.account?.name || "",
          card_type: t.card_type || null,
          card_last4: t.card_last4 || null,
          source: "transaction",
          txn_id: t.id,
          reservation_id: t.reservation || null,
          entity_type: entityType,
          entity_id: entityId,
          entity_name: entityName,
        });
      });
    }

    if (movRes.data) {
      movRes.data.forEach((m) => {
        const res = m.reservation_id ? resById[m.reservation_id] : null;
        let entityType = null;
        let entityId = null;
        let entityName = "";
        if (res?.company) {
          entityType = "company";
          entityId = res.company;
          entityName = companiesById[res.company] || "Empresa";
        } else if (res?.customer) {
          entityType = "customer";
          entityId = res.customer;
          entityName = customersById[res.customer] || "Cliente";
        }
        allMovements.push({
          id: `mov-${m.id}`,
          time: m.created_at,
          type: m.type,
          description: m.description,
          amount: m.amount,
          payment_method: m.payment_method || "",
          account: m.account?.name || "",
          to_account: m.to_account?.name || "",
          source: "cash_movement",
          reservation_id: m.reservation_id || null,
          entity_type: entityType,
          entity_id: entityId,
          entity_name: entityName,
        });
      });
    }

    // For payment rows, resolve how many reservations each one covers so the
    // Reserva cell can show "#N" (one reservation) vs "Ver detalle" (many).
    const paymentTxnIds = allMovements
      .filter((m) => m.source === "transaction" && m.type === "income")
      .map((m) => m.txn_id);
    if (paymentTxnIds.length > 0) {
      const { data: summary } = await supabase.rpc(
        "get_payment_reservation_summary",
        { p_txn_ids: paymentTxnIds }
      );
      const byTxn = Object.fromEntries(
        (summary || []).map((s) => [s.transaction_id, s])
      );
      allMovements.forEach((m) => {
        if (m.source === "transaction" && m.type === "income") {
          const s = byTxn[m.txn_id];
          m.res_count = s?.reservation_count || 0;
          m.single_res_id = s?.reservation_id || null;
        }
      });
    }

    allMovements.sort((a, b) => new Date(b.time) - new Date(a.time));
    setMovements(allMovements);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  const saveMovement = async () => {
    const { error } = await supabase.from("cash_movements").insert({
      type: newMovement.type,
      amount: parseFloat(newMovement.amount) || 0,
      description: newMovement.description,
      payment_method: newMovement.payment_method,
      account_id: parseInt(newMovement.account_id) || null,
      created_by: session?.user?.id,
    });
    if (error) {
      alert("Error: " + error.message);
      return;
    }
    setAddModal(false);
    setNewMovement({ type: "expense", amount: "", description: "", payment_method: "Efectivo", account_id: "" });
    fetchData();
  };

  const saveTransfer = async () => {
    const amount = parseFloat(newTransfer.amount) || 0;
    const { error } = await supabase.from("cash_movements").insert({
      type: "transfer",
      amount,
      description: newTransfer.description || "Transferencia entre cuentas",
      account_id: parseInt(newTransfer.from_account),
      to_account_id: parseInt(newTransfer.to_account),
      created_by: session?.user?.id,
    });
    if (error) {
      alert("Error: " + error.message);
      return;
    }
    setTransferModal(false);
    setNewTransfer({ amount: "", description: "", from_account: "", to_account: "" });
    fetchData();
  };

  const typeBadge = (type) => {
    switch (type) {
      case "income":
        return <Badge tone="success">Ingreso</Badge>;
      case "expense":
        return <Badge tone="critical">Gasto</Badge>;
      case "deposit":
        return <Badge tone="info">Depósito</Badge>;
      case "adjustment":
        return <Badge tone="warning">Ajuste</Badge>;
      case "transfer":
        return <Badge tone="info">Transferencia</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  // Calculate daily totals (deposits excluded from income/expense — they're collateral)
  const totals = movements.reduce(
    (acc, m) => {
      if (m.type === "income") acc.income += m.amount;
      else if (m.type === "expense") acc.expense += m.amount;
      else if (m.type === "deposit") acc.deposits += m.amount;
      return acc;
    },
    { income: 0, expense: 0, deposits: 0 }
  );

  // Per-account daily net: income + deposit - expense + transfers in - transfers out
  // For card accounts, also track credit vs debit subtotals
  const accountDailyTotals = {};
  const accountCardBreakdown = {};
  accounts.forEach((a) => {
    accountDailyTotals[a.name] = 0;
    accountCardBreakdown[a.name] = { credit: 0, debit: 0 };
  });
  movements.forEach((m) => {
    if (m.type === "income" || m.type === "deposit" || m.type === "adjustment") {
      if (accountDailyTotals[m.account] !== undefined)
        accountDailyTotals[m.account] += m.amount;
      if (m.card_type && accountCardBreakdown[m.account])
        accountCardBreakdown[m.account][m.card_type] += m.amount;
    } else if (m.type === "expense") {
      if (accountDailyTotals[m.account] !== undefined)
        accountDailyTotals[m.account] -= m.amount;
      if (m.card_type && accountCardBreakdown[m.account])
        accountCardBreakdown[m.account][m.card_type] -= m.amount;
    } else if (m.type === "transfer") {
      if (accountDailyTotals[m.account] !== undefined)
        accountDailyTotals[m.account] -= m.amount;
      if (accountDailyTotals[m.to_account] !== undefined)
        accountDailyTotals[m.to_account] += m.amount;
    }
  });

  const accountOptions = accounts.map((a) => ({
    label: a.name,
    value: String(a.id),
  }));

  return (
    <AppWrapper>
      <Page
        title="Caja Diaria"
        primaryAction={
          <Button variant="primary" onClick={() => setAddModal(true)}>
            + Movimiento
          </Button>
        }
        secondaryActions={[
          { content: "Transferencia", onAction: () => setTransferModal(true) },
          { content: "Imprimir", onAction: () => window.print() },
        ]}
      >
        <BlockStack gap="400">
          <TextField
            label="Fecha"
            type="date"
            value={date}
            onChange={setDate}
          />

          {/* Per-account daily total */}
          <InlineGrid columns={accounts.length || 1} gap="300">
            {accounts.map((acc) => {
              const total = accountDailyTotals[acc.name] || 0;
              const breakdown = accountCardBreakdown[acc.name] || { credit: 0, debit: 0 };
              const isCard = acc.type === "card";
              return (
                <Card key={acc.id}>
                  <Text variant="bodySm" tone="subdued">
                    {acc.name}
                  </Text>
                  <Text
                    variant="headingLg"
                    tone={total > 0 ? "success" : total < 0 ? "critical" : undefined}
                  >
                    {total < 0 ? "-" : ""}
                    {formatPrice(Math.abs(total))}
                  </Text>
                  {isCard && (breakdown.credit !== 0 || breakdown.debit !== 0) && (
                    <div style={{ marginTop: 4 }}>
                      <Text variant="bodySm" tone="subdued">
                        Crédito: {formatPrice(breakdown.credit)}
                      </Text>
                      <Text variant="bodySm" tone="subdued">
                        Débito: {formatPrice(breakdown.debit)}
                      </Text>
                    </div>
                  )}
                </Card>
              );
            })}
          </InlineGrid>

          {/* Daily summary */}
          <InlineGrid columns={4} gap="300">
            <Card>
              <Text variant="bodySm" tone="subdued">Ingresos</Text>
              <Text variant="headingMd" tone="success">
                {formatPrice(totals.income)}
              </Text>
            </Card>
            <Card>
              <Text variant="bodySm" tone="subdued">Gastos / Reembolsos</Text>
              <Text variant="headingMd" tone="critical">
                {formatPrice(totals.expense)}
              </Text>
            </Card>
            <Card>
              <Text variant="bodySm" tone="subdued">Depósitos (colateral)</Text>
              <Text variant="headingMd">
                {formatPrice(totals.deposits)}
              </Text>
            </Card>
            <Card>
              <Text variant="bodySm" tone="subdued">Neto del día</Text>
              <Text variant="headingMd">
                {formatPrice(totals.income - totals.expense)}
              </Text>
            </Card>
          </InlineGrid>

          {/* Movements table */}
          {loading ? (
            <Spinner size="small" />
          ) : (
            <LegacyCard>
              <IndexTable
                resourceName={{ singular: "Movimiento", plural: "Movimientos" }}
                selectable={false}
                itemCount={movements.length}
                headings={[
                  { title: "Hora" },
                  { title: "Descripción" },
                  { title: "Cliente / Empresa" },
                  { title: "Reserva" },
                  { title: "Tipo" },
                  { title: "Cuenta" },
                  { title: "Monto" },
                ]}
              >
                {movements.map((m, i) => (
                  <IndexTable.Row
                    key={m.id}
                    id={m.id}
                    position={i}
                    onClick={() => setMovementDetail({ open: true, movement: m })}
                  >
                    <IndexTable.Cell>
                      <Text variant="bodySm">
                        {m.time ? moment(m.time).format("HH:mm") : "-"}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm">{m.description}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {m.entity_id && m.entity_name ? (
                        <Link
                          to={
                            m.entity_type === "company"
                              ? `/companies/edit?id=${m.entity_id}`
                              : `/customers/edit?id=${m.entity_id}`
                          }
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: "#005bd3", textDecoration: "none" }}
                        >
                          {m.entity_name}
                        </Link>
                      ) : (
                        <Text variant="bodySm" tone="subdued">—</Text>
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {m.source === "transaction" &&
                      m.type === "income" &&
                      m.res_count > 0 ? (
                        <span
                          role="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailTxnId(m.txn_id);
                          }}
                          style={{
                            color: "#005bd3",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          {m.res_count === 1 && m.single_res_id
                            ? `#${m.single_res_id}`
                            : "Ver detalle"}
                        </span>
                      ) : m.reservation_id ? (
                        <Link
                          to={`/reservation?id=${m.reservation_id}`}
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: "#005bd3", textDecoration: "none" }}
                        >
                          #{m.reservation_id}
                        </Link>
                      ) : (
                        <Text variant="bodySm" tone="subdued">—</Text>
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>{typeBadge(m.type)}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm">
                        {m.type === "transfer"
                          ? `${m.account} → ${m.to_account}`
                          : m.account || m.payment_method}
                        {m.card_type ? (
                          <Text as="span" tone="subdued">
                            {" · "}
                            {m.card_type === "credit" ? "Crédito" : "Débito"}
                          </Text>
                        ) : null}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text
                        variant="bodySm"
                        tone={
                          m.type === "income"
                            ? "success"
                            : m.type === "expense"
                              ? "critical"
                              : undefined
                        }
                      >
                        {m.type === "expense" ? "-" : ""}
                        {formatPrice(m.amount)}
                      </Text>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </LegacyCard>
          )}
        </BlockStack>

        {/* Add movement modal */}
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          title="Nuevo Movimiento"
          primaryAction={{
            content: "Guardar",
            onAction: saveMovement,
            disabled: !newMovement.amount || !newMovement.description,
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setAddModal(false) }]}
        >
          <Modal.Section>
            <FormLayout>
              <Select
                label="Tipo"
                value={newMovement.type}
                options={[
                  { label: "Gasto", value: "expense" },
                  { label: "Ingreso", value: "income" },
                  { label: "Ajuste", value: "adjustment" },
                ]}
                onChange={(val) => setNewMovement({ ...newMovement, type: val })}
              />
              <TextField
                label="Descripción"
                value={newMovement.description}
                onChange={(val) => setNewMovement({ ...newMovement, description: val })}
              />
              <TextField
                label="Monto"
                type="number"
                prefix="$"
                value={newMovement.amount}
                onChange={(val) => setNewMovement({ ...newMovement, amount: val })}
              />
              <Select
                label="Cuenta"
                value={newMovement.account_id}
                options={[{ label: "Seleccionar", value: "" }, ...accountOptions]}
                onChange={(val) => setNewMovement({ ...newMovement, account_id: val })}
              />
            </FormLayout>
          </Modal.Section>
        </Modal>

        {/* Transfer modal */}
        <Modal
          open={transferModal}
          onClose={() => setTransferModal(false)}
          title="Transferencia entre Cuentas"
          primaryAction={{
            content: "Transferir",
            onAction: saveTransfer,
            disabled: !newTransfer.amount || !newTransfer.from_account || !newTransfer.to_account,
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setTransferModal(false) }]}
        >
          <Modal.Section>
            <FormLayout>
              <Select
                label="De"
                value={newTransfer.from_account}
                options={[{ label: "Seleccionar", value: "" }, ...accountOptions]}
                onChange={(val) => setNewTransfer({ ...newTransfer, from_account: val })}
              />
              <Select
                label="A"
                value={newTransfer.to_account}
                options={[{ label: "Seleccionar", value: "" }, ...accountOptions]}
                onChange={(val) => setNewTransfer({ ...newTransfer, to_account: val })}
              />
              <TextField
                label="Monto"
                type="number"
                prefix="$"
                value={newTransfer.amount}
                onChange={(val) => setNewTransfer({ ...newTransfer, amount: val })}
              />
              <TextField
                label="Descripción"
                value={newTransfer.description}
                onChange={(val) => setNewTransfer({ ...newTransfer, description: val })}
                placeholder="Transferencia entre cuentas"
              />
            </FormLayout>
          </Modal.Section>
        </Modal>

        <PaymentDetailModal
          open={detailTxnId != null}
          transactionId={detailTxnId}
          onClose={() => setDetailTxnId(null)}
        />

        <Modal
          open={movementDetail.open}
          onClose={() => setMovementDetail({ open: false, movement: null })}
          title="Detalle del movimiento"
          secondaryActions={[
            {
              content: "Cerrar",
              onAction: () =>
                setMovementDetail({ open: false, movement: null }),
            },
          ]}
        >
          <Modal.Section>
            {movementDetail.movement &&
              (() => {
                const m = movementDetail.movement;
                const cardLabel = m.card_type
                  ? m.card_type === "credit"
                    ? "Tarjeta de crédito"
                    : "Tarjeta de débito"
                  : null;
                return (
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text tone="subdued">
                        {m.time ? moment(m.time).format("D [de] MMM YYYY, HH:mm") : "—"}
                      </Text>
                      {typeBadge(m.type)}
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text variant="headingMd">Monto</Text>
                      <Text variant="headingMd">{formatPrice(m.amount)}</Text>
                    </InlineStack>
                    <Divider />
                    <InlineStack align="space-between">
                      <Text>Cuenta</Text>
                      <Text>
                        {m.type === "transfer"
                          ? `${m.account} → ${m.to_account}`
                          : m.account || m.payment_method || "—"}
                      </Text>
                    </InlineStack>
                    {cardLabel && (
                      <InlineStack align="space-between">
                        <Text>{cardLabel}</Text>
                        <Text>
                          {m.card_last4
                            ? `•••• ${m.card_last4}`
                            : "Sin registrar"}
                        </Text>
                      </InlineStack>
                    )}
                    {m.description && (
                      <InlineStack align="space-between" blockAlign="start">
                        <Text>Concepto</Text>
                        <div style={{ maxWidth: "60%", textAlign: "right" }}>
                          <Text>{m.description}</Text>
                        </div>
                      </InlineStack>
                    )}
                    {m.entity_name && (
                      <InlineStack align="space-between">
                        <Text>
                          {m.entity_type === "company" ? "Empresa" : "Cliente"}
                        </Text>
                        {m.entity_id ? (
                          <Link
                            to={
                              m.entity_type === "company"
                                ? `/companies/edit?id=${m.entity_id}`
                                : `/customers/edit?id=${m.entity_id}`
                            }
                            style={{ color: "#005bd3", textDecoration: "none" }}
                          >
                            {m.entity_name}
                          </Link>
                        ) : (
                          <Text>{m.entity_name}</Text>
                        )}
                      </InlineStack>
                    )}
                    {m.reservation_id && (
                      <InlineStack align="space-between">
                        <Text>Reserva</Text>
                        <Link
                          to={`/reservation?id=${m.reservation_id}`}
                          style={{ color: "#005bd3", textDecoration: "none" }}
                        >
                          #{m.reservation_id}
                        </Link>
                      </InlineStack>
                    )}
                    {m.source === "transaction" &&
                      m.type === "income" &&
                      m.res_count > 0 && (
                        <>
                          <Divider />
                          <span
                            role="button"
                            onClick={() => {
                              setDetailTxnId(m.txn_id);
                              setMovementDetail({ open: false, movement: null });
                            }}
                            style={{
                              color: "#005bd3",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            Ver facturas y reservas asociadas
                          </span>
                        </>
                      )}
                  </BlockStack>
                );
              })()}
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default CashDaily;
